import React, { FC } from "react";
import { Timeline, timelineItemClasses } from "@mui/lab";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ForbiddenTooltip from "../../../../../../../components/common/ForbiddenTooltip";
import { EntityItemDTO, TripArcDTO } from "../../../../../../../datasources/api-client/data-contracts";
import ArcDetails from "./ArcDetails";
import NodeDetails from "./NodeDetails";

interface Props {
	arcs?: TripArcDTO[];
	isLoading: boolean;
	isEntity?: boolean;
	visibleEntities?: string[];
	entitiesScrollRef?: React.RefObject<HTMLDivElement | null>;
	action?: {
		title: string;
		onClick: () => void;
		forbidden?: boolean;
	};
}

const TripEmissionDetails: FC<Props> = ({
	arcs,
	isLoading,
	isEntity = false,
	action,
	visibleEntities = [],
	entitiesScrollRef,
}) => {
	const { t } = useTranslation("feature.results");

	const headingTitle = isEntity
		? t("tripDetails/entity emissions details")
		: t("tripDetails/transport emissions details");

	const heading = (
		<Stack direction="row" mb={2}>
			<Typography variant="h5" color="textPrimary" mb={1}>
				{headingTitle}
			</Typography>
			<Box sx={{ flexGrow: 1 }} />
			{!isLoading && action && (
				<ForbiddenTooltip forbidden={!!action.forbidden} title={t("common:content locked/action forbidden")}>
					<Button variant="contained" onClick={action.onClick} disabled={action.forbidden}>
						{action.title}
					</Button>
				</ForbiddenTooltip>
			)}
		</Stack>
	);

	if (isLoading)
		return (
			<>
				{heading}
				<Skeleton variant="rounded" height="100%" width="100%" />
			</>
		);

	if (!arcs || arcs.length === 0) return <></>;

	const startNode = arcs[0].origin;

	let firstVisibleEntitiesRefAssigned = false;

	return (
		<>
			{heading}
			<Timeline
				sx={{
					width: "100%",
					padding: 0,
					[`& .${timelineItemClasses.root}:before`]: {
						flex: 0,
					},
				}}
			>
				<NodeDetails node={startNode} nodeType="start" hubEmission={arcs[0].originTCo2e} />

				{arcs.map((arc, index) => {
					const nodeType = index === arcs.length - 1 ? "end" : "transit";

					const hasVisibleEntities = (arc.entities ?? []).some((entity: EntityItemDTO) =>
						visibleEntities.includes(entity.id)
					);

					const shouldAttachEntitiesRef = !firstVisibleEntitiesRefAssigned && hasVisibleEntities;

					if (shouldAttachEntitiesRef) {
						firstVisibleEntitiesRefAssigned = true;
					}

					return (
						<React.Fragment key={index}>
							<ArcDetails
								arc={arc}
								visibleEntities={visibleEntities}
								entitiesScrollRef={shouldAttachEntitiesRef ? entitiesScrollRef : undefined}
							/>
							<NodeDetails
								node={arc.destination}
								nodeType={nodeType}
								hubEmission={arc.destinationTCo2e}
							/>
						</React.Fragment>
					);
				})}
			</Timeline>
		</>
	);
};

export default TripEmissionDetails;
